// Peças reutilizáveis da landing /emagrecimento: revelar ao rolar e contador
// animado. (O "rastreador de valor" saiu em 23/10/2026, a pedido do dono.)
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

export const prefersReducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** true quando o elemento entra na tela (uma vez, por padrão). */
export function useInView<T extends Element = HTMLDivElement>({
  threshold = 0.18,
  rootMargin = "0px 0px -8% 0px",
  once = true,
}: { threshold?: number; rootMargin?: string; once?: boolean } = {}) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          if (once) io.disconnect();
        } else if (!once) setInView(false);
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin, once]);
  return [ref, inView] as const;
}

/** Bloco que aparece suavemente quando entra na tela. */
export function Reveal({
  children,
  delay = 0,
  variant = "up",
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  variant?: "up" | "left" | "right" | "scale";
  className?: string;
}) {
  const [ref, inView] = useInView<HTMLDivElement>();
  return (
    <div
      ref={ref}
      data-v={variant}
      className={`em-reveal ${inView ? "em-in" : ""} ${className}`}
      style={{ "--d": `${delay}ms` } as CSSProperties}
    >
      {children}
    </div>
  );
}

/** Número que "corre" até o alvo; continua de onde estava se o alvo mudar. */
export function useCountUp(target: number, active: boolean, duration = 900) {
  const [val, setVal] = useState(0);
  const cur = useRef(0);
  useEffect(() => {
    if (!active) return;
    if (prefersReducedMotion()) {
      cur.current = target;
      setVal(target);
      return;
    }
    const from = cur.current;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      const e = 1 - Math.pow(1 - p, 3);
      const v = from + (target - from) * e;
      cur.current = v;
      setVal(v);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return val;
}

/** Título e descrição da LP e do checkout do Programa: sem termo de saúde, sem remédio (doc do Paulo, 04/11/2026). */
export const PROGRAMA_SEO = {
  title: "Programa Blink · acompanhamento médico e nutricional online",
  description: "Programa de 2 meses com acompanhamento médico e nutricional online e suporte da equipe Blink.",
};
